import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { formatDate, formatDateTime } from "../lib/dateTime";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { PageLoadError, isForbidden } from "./NoAccess";
import { useScreenWrites } from "./screenWrites";
import { Loader2, Sparkles, RefreshCcw, Send, MessageSquare, History as HistoryIcon } from "lucide-react";

/**
 * The sentence for a failed AI request. A 403 is the role (generating and chatting are
 * `data.insights.write`); a 404 on a session is one the caller does not own or that does not
 * exist, which the server answers alike (hq-customers-contract.md 3.2).
 */
const aiErrorMessage = (e, t, fallbackKey) => {
  if (isForbidden(e)) return t("access.forbiddenAction");
  if (e?.response?.status === 404) return t("hqai.sessionUnavailable");
  return t(fallbackKey);
};

export default function HqAi() {
  const t = useT("superadmin");
  // Generating a briefing and chatting store rows and spend a model call.
  const { generate: canGenerate } = useScreenWrites("ai");
  const [tab, setTab] = useState("insights");
  return (
    <div data-testid="hq-ai">
      <h1 className="font-sans font-semibold text-5xl mb-2 text-slate-800">{t("hqai.title")}</h1>
      <p className="text-slate-500 mb-6">{t("hqai.subtitle")}</p>
      <div className="flex gap-2 mb-6">
        <button onClick={() => setTab("insights")} data-testid="tab-insights" className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm ${tab === "insights" ? "bg-sol-gold/15 text-amber-600 border border-sol-gold/30" : "bg-white text-slate-500 border border-slate-200"}`}>
          <Sparkles className="w-4 h-4" /> {t("hqai.tabInsights")}
        </button>
        <button onClick={() => setTab("agent")} data-testid="tab-agent" className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm ${tab === "agent" ? "bg-sol-gold/15 text-amber-600 border border-sol-gold/30" : "bg-white text-slate-500 border border-slate-200"}`}>
          <MessageSquare className="w-4 h-4" /> {t("hqai.tabAgent")}
        </button>
      </div>
      {tab === "insights" ? <InsightsTab t={t} canGenerate={canGenerate} /> : <AgentTab t={t} canChat={canGenerate} />}
    </div>
  );
}

function InsightsTab({ t, canGenerate }) {
  const { lang } = useLanguage();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const load = () => {
    setLoadError(null);
    return api.get("/super-admin/hq/insights").then((r) => setItems(r.data.items)).catch(setLoadError);
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      await api.post("/super-admin/hq/insights/generate");
      toast.success(t("hqai.briefingGenerated")); load();
    } catch (e) { toast.error(aiErrorMessage(e, t, "hqai.error")); }
    finally { setBusy(false); }
  };

  return (
    <div>
      {canGenerate && (
        <Button onClick={generate} disabled={busy} className="bg-sol-gold text-black hover:bg-amber-200 mb-6" data-testid="generate-insight">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCcw className="w-4 h-4 mr-2" /> {t("hqai.generateBriefing")}</>}
        </Button>
      )}
      <PageLoadError error={loadError} onRetry={load} testId="insights-load-error" />
      <div className="space-y-4">
        {items.map((it) => (
          <div key={it.insight_id} className="rounded-2xl bg-white border border-slate-200 p-6" data-testid={`insight-${it.insight_id}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 text-xs text-amber-600">
                <Sparkles className="w-4 h-4" /> {t("hqai.briefing")}
              </div>
              <div className="text-xs text-slate-400">{formatDateTime(it.generated_at, lang)} · {it.generated_by}</div>
            </div>
            <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">{it.briefing}</pre>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              {Object.entries(it.stats || {}).map(([k, v]) => (
                <div key={k} className="bg-slate-50 rounded-lg px-3 py-2"><div className="text-slate-400">{k}</div><div className="font-medium text-amber-600">{v}</div></div>
              ))}
            </div>
          </div>
        ))}
        {!loadError && items.length === 0 && <p className="text-slate-400 text-sm py-12 text-center">{t(canGenerate ? "hqai.noInsights" : "hqai.noInsightsReadOnly")}</p>}
      </div>
    </div>
  );
}

function AgentTab({ t, canChat }) {
  const { lang } = useLanguage();
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const [sessionsError, setSessionsError] = useState(null);
  const loadSessions = () => {
    setSessionsError(null);
    return api.get("/super-admin/hq/agent/sessions").then((r) => setSessions(r.data)).catch(setSessionsError);
  };
  useEffect(() => { loadSessions(); }, []);
  // A session that answers 404 is not the caller's (or no longer exists): it is dropped from the
  // screen and the list is read again, so it stops being offered.
  const forgetSession = () => { setActive(null); setMessages([]); loadSessions(); };
  useEffect(() => {
    if (!active) { setMessages([]); return; }
    let current = true;
    api.get(`/super-admin/hq/agent/sessions/${active}/messages`)
      .then((r) => { if (current) setMessages(r.data); })
      .catch((e) => {
        if (!current) return;
        toast.error(aiErrorMessage(e, t, "hqai.errorAgent"));
        if (e?.response?.status === 404) forgetSession();
      });
    return () => { current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, t]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages]);

  const send = async () => {
    if (!input.trim()) return;
    setBusy(true);
    const msgText = input;
    setInput("");
    setMessages([...messages, { role: "user", content: msgText, at: new Date().toISOString() }]);
    try {
      const r = await api.post("/super-admin/hq/agent/chat", { session_id: active, message: msgText });
      setActive(r.data.session_id);
      setMessages((cur) => [...cur, { role: "assistant", content: r.data.reply, at: new Date().toISOString() }]);
      loadSessions();
    } catch (e) {
      toast.error(aiErrorMessage(e, t, "hqai.errorAgent"));
      if (e?.response?.status === 404) forgetSession();
    }
    finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-[280px_1fr] gap-4" style={{ height: "calc(100vh - 280px)" }}>
      <div className="rounded-2xl bg-white border border-slate-200 p-4 overflow-y-auto">
        {canChat && (
          <Button onClick={() => { setActive(null); setMessages([]); }} variant="outline" className="w-full border-slate-200 text-slate-800/70 mb-3" data-testid="new-session">
            {t("hqai.newConversation")}
          </Button>
        )}
        <div className="text-xs uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5"><HistoryIcon className="w-3 h-3" /> {t("hqai.sessions")}</div>
        <div className="space-y-1">
          {sessions.map((s) => (
            <button key={s.session_id} onClick={() => setActive(s.session_id)} data-testid={`session-${s.session_id}`}
              className={`w-full text-left px-3 py-2 rounded-lg text-xs ${active === s.session_id ? "bg-sol-gold/10 text-amber-600" : "text-slate-500 hover:bg-white/[0.04]"}`}>
              <div className="truncate font-medium">{s.last_message || t("hqai.emptySession")}</div>
              <div className="text-[10px] text-slate-400">{s.message_count || 0} {t("hqai.msgs")} · {formatDate(s.updated_at, lang)}</div>
            </button>
          ))}
          <PageLoadError error={sessionsError} onRetry={loadSessions} testId="sessions-load-error" />
          {!sessionsError && sessions.length === 0 && <div className="text-xs text-slate-400 px-2 py-4">{t("hqai.noSessions")}</div>}
        </div>
      </div>
      <div className="rounded-2xl bg-white border border-slate-200 flex flex-col overflow-hidden">
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="chat-messages">
          {messages.length === 0 && <p className="text-slate-400 text-sm text-center mt-12">{t(canChat ? "hqai.agentPrompt" : "hqai.agentReadOnly")}</p>}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm ${m.role === "user" ? "bg-sol-gold/15 text-slate-800" : "bg-white/[0.04] text-slate-700"}`}>
                <div className="whitespace-pre-wrap leading-relaxed">{m.content}</div>
              </div>
            </div>
          ))}
          {busy && <Loader2 className="w-4 h-4 animate-spin text-amber-600" />}
        </div>
        {canChat && (
        <div className="p-4 border-t border-slate-100 flex gap-2">
          <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
            placeholder={t("hqai.askPlaceholder")} className="bg-white border-slate-200" data-testid="chat-input" />
          <Button onClick={send} disabled={busy || !input.trim()} className="bg-sol-gold text-black hover:bg-amber-200" data-testid="chat-send">
            <Send className="w-4 h-4" />
          </Button>
        </div>
        )}
      </div>
    </div>
  );
}
